import { DataEndChannel } from 'src/app/project/shared/interfaces/data-end-channel.interface';
import { DataEndChannelTypeEnum } from 'src/app/project/shared/enums/data-end-channel-type.enum';
import { SchemaRadarInfo } from 'src/app/project/project-schema/shared/interfaces/schema-route-station-info.interface';

/** Beschriftung einer Komponente (DataEndChannel) für die SWA-Visualisierung. */
export interface ChannelLabel {
  name: string;
  sublabel?: string;
}

/**
 * DE-Nummern ab hier sind reservierte/virtuelle Datenendpunkte (keine echten,
 * anzeigbaren Bauteile) und werden nicht dargestellt.
 */
export const DE_RESERVED_FROM = 190;

/**
 * Fahrspur-Beschriftung, abgeleitet aus der Datenendpunkt-Nummer (DE):
 * DE1 = Hauptfahrspur, DE2 = 1. Überholspur, DE3 = 2. Überholspur …
 *
 * Neu vereinbarte Übergangsregel, bis eine explizite Fahrspur-Markierung
 * eingeführt wird. Bewusst KEINE Sonderbehandlung fester DE-Bereiche (z. B.
 * der historische ASIM/ADEC-Offset 160) — die Spur ergibt sich allein aus DE.
 */
export function getLaneLabel(de: number): string {
  return de === 1 ? 'Hauptfahrspur' : `${de - 1}. Überholspur`;
}

/**
 * Konkreter Sensor-/Radartyp eines RADAR-Kanals über die Verknüpfung
 * DataEndChannel -> Radar -> RadarType (localbus-frei):
 * schemaInfo.radars[].data.radarType.dataEndChannelRadars[].dataEndChannelId === channel.id.
 * Liefert den `radarType.name` (z. B. „ADEC TDC1-PIR") oder null, wenn keine
 * Zuordnung existiert bzw. der Kanal kein RADAR ist.
 */
export function getRadarTypeName(channel: DataEndChannel, radars?: SchemaRadarInfo[]): string | null {
  if (channel.dataEndChannelType !== DataEndChannelTypeEnum.RADAR) { return null; }
  const match = (radars ?? []).find(radar =>
    radar.data?.radarType?.dataEndChannelRadars?.some(link => link.dataEndChannelId === channel.id)
  );
  return match?.data?.radarType?.name || null;
}

/**
 * Liefert die fachliche Beschriftung eines DataEndChannels für die SWA-Ansicht.
 * Gibt null zurück, wenn die Komponente nicht dargestellt werden soll.
 *
 * Der Komponententyp wird über `dataEndChannelType` bestimmt (NICHT über
 * fg/localbusAddress). Der konkrete Sensortyp eines RADAR-Kanals kommt aus der
 * Radar-Verknüpfung (`getRadarTypeName`); die Fahrspur aus der DE-Nummer.
 */
export function getChannelLabel(channel: DataEndChannel, radars?: SchemaRadarInfo[]): ChannelLabel | null {
  if (channel.de >= DE_RESERVED_FROM) { return null; }

  switch (channel.dataEndChannelType) {
    case DataEndChannelTypeEnum.RADAR:
      return { name: getRadarTypeName(channel, radars) ?? 'Sensor', sublabel: getLaneLabel(channel.de) };
    case DataEndChannelTypeEnum.BLINKER:
      return { name: 'Blinker' };
    case DataEndChannelTypeEnum.LED:
      return { name: 'Anzeige' };
    case DataEndChannelTypeEnum.PW:
      return { name: 'Prismenwender' };
    case DataEndChannelTypeEnum.DIGITALINPUT:
      return { name: `Eingang ${channel.de}` };
    case DataEndChannelTypeEnum.DIGITALOUTPUT:
      return { name: `Ausgang ${channel.de}` };
    default:
      return null;
  }
}

/**
 * Wirkrichtung einer Komponente innerhalb einer Line – abgeleitet aus dem Komponententyp
 * (dataEndChannelType):
 * - 'source' = steuert (RADAR/MQ, digitaler Eingang)
 * - 'target' = wird gesteuert (LED/AQ, Prismenwender, Blinker, digitaler Ausgang)
 * Cluster-Typen gehören nicht zur SWA und werden nicht klassifiziert.
 */
export type ChannelRole = 'source' | 'target';

export function getChannelRole(channel: DataEndChannel): ChannelRole | null {
  switch (channel.dataEndChannelType) {
    case DataEndChannelTypeEnum.RADAR:
    case DataEndChannelTypeEnum.DIGITALINPUT:
      return 'source';
    case DataEndChannelTypeEnum.LED:
    case DataEndChannelTypeEnum.PW:
    case DataEndChannelTypeEnum.BLINKER:
    case DataEndChannelTypeEnum.DIGITALOUTPUT:
      return 'target';
    default:
      return null;
  }
}
